import {
  forwardRef,
  useState,
  useRef,
  useCallback,
  type InputHTMLAttributes,
} from "react";

export interface SliderProps
  extends Omit<
    InputHTMLAttributes<HTMLInputElement>,
    "type" | "onChange" | "value"
  > {
  /** Current value (0-100) */
  value: number;
  /** Callback when value changes */
  onChange: (value: number) => void;
  /** Callback when user finishes dragging */
  onChangeEnd?: (value: number) => void;
  /** Label text */
  label: string;
  /** Optional description */
  description?: string;
  /** Minimum value */
  min?: number;
  /** Maximum value */
  max?: number;
  /** Step increment */
  step?: number;
  /** Show value as percentage */
  showValue?: boolean;
  /** Custom icon to show on the left */
  icon?: React.ReactNode;
}

/**
 * Slider - A whimsical volume/range slider for settings.
 *
 * Features a gradient-filled track with sparkle effects
 * and a magical thumb design.
 *
 * @example
 * <Slider
 *   value={volume}
 *   onChange={setVolume}
 *   onChangeEnd={playTestSound}
 *   label="Master Volume"
 *   icon={<VolumeIcon />}
 * />
 */
export const Slider = forwardRef<HTMLInputElement, SliderProps>(
  (
    {
      value,
      onChange,
      onChangeEnd,
      label,
      description,
      min = 0,
      max = 100,
      step = 1,
      showValue = true,
      icon,
      disabled = false,
      id,
      className = "",
      ...props
    },
    ref
  ) => {
    const sliderId = id || `slider-${label.toLowerCase().replace(/\s+/g, "-")}`;
    const [isDragging, setIsDragging] = useState(false);
    const trackRef = useRef<HTMLDivElement>(null);

    // Calculate percentage for styling
    const percentage = ((value - min) / (max - min)) * 100;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = parseInt(e.target.value, 10);
      onChange(newValue);
    };

    const handleDragEnd = useCallback(() => {
      setIsDragging(false);
      onChangeEnd?.(value);
    }, [onChangeEnd, value]);

    const handleDragStart = () => {
      setIsDragging(true);
    };

    // Get volume icon based on level
    const getVolumeIcon = () => {
      if (icon) return icon;

      // Default volume icon changes based on level
      if (value === 0) {
        return (
          <svg
            className="w-6 h-6 text-neutral-400"
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" />
          </svg>
        );
      }
      if (value < 50) {
        return (
          <svg
            className="w-6 h-6 text-magic-500"
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <path d="M18.5 12c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM5 9v6h4l5 5V4L9 9H5z" />
          </svg>
        );
      }
      return (
        <svg
          className="w-6 h-6 text-magic-600"
          viewBox="0 0 24 24"
          fill="currentColor"
        >
          <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
        </svg>
      );
    };

    return (
      <div className={`space-y-3 ${className}`}>
        {/* Header with label and value */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {getVolumeIcon()}
            <div>
              <label
                htmlFor={sliderId}
                className={`
                  block font-body font-semibold text-body-lg text-neutral-800
                  ${disabled ? "opacity-50" : ""}
                `}
              >
                {label}
              </label>
              {description && (
                <p className="font-body text-body-md text-neutral-500">
                  {description}
                </p>
              )}
            </div>
          </div>

          {showValue && (
            <span
              className={`
                font-display font-bold text-body-lg
                min-w-[3.5rem] text-right
                transition-colors duration-200
                ${value > 0 ? "text-magic-600" : "text-neutral-400"}
              `}
            >
              {value}%
            </span>
          )}
        </div>

        {/* Slider track */}
        <div className="relative pt-1 pb-2" ref={trackRef}>
          {/* Background track */}
          <div
            className={`
              absolute top-1/2 -translate-y-1/2 left-0 right-0
              h-3 rounded-full
              bg-neutral-200
              shadow-[inset_0_2px_4px_rgba(0,0,0,0.1)]
              overflow-hidden
              ${disabled ? "opacity-50" : ""}
            `}
          >
            {/* Filled portion with gradient */}
            <div
              className="absolute inset-y-0 left-0 rounded-full transition-all duration-150"
              style={{
                width: `${percentage}%`,
                background: disabled
                  ? "rgb(200, 200, 200)"
                  : `linear-gradient(90deg,
                      rgba(155, 89, 182, 0.8) 0%,
                      rgba(155, 89, 182, 1) 50%,
                      rgba(180, 120, 200, 1) 100%)`,
                boxShadow: disabled
                  ? "none"
                  : "0 0 12px rgba(155, 89, 182, 0.4)",
              }}
            >
              {/* Shimmer effect */}
              {!disabled && percentage > 0 && (
                <div
                  className="absolute inset-0 opacity-30"
                  style={{
                    background:
                      "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.6) 50%, transparent 100%)",
                    animation: isDragging
                      ? "none"
                      : "shimmer 2s ease-in-out infinite",
                  }}
                />
              )}
            </div>
          </div>

          {/* Native range input (invisible, for accessibility) */}
          <input
            ref={ref}
            type="range"
            id={sliderId}
            min={min}
            max={max}
            step={step}
            value={value}
            onChange={handleChange}
            onMouseDown={handleDragStart}
            onTouchStart={handleDragStart}
            onMouseUp={handleDragEnd}
            onTouchEnd={handleDragEnd}
            disabled={disabled}
            className={`
              absolute inset-0
              w-full h-full
              opacity-0
              cursor-pointer
              ${disabled ? "cursor-not-allowed" : ""}
            `}
            aria-label={label}
            aria-valuenow={value}
            aria-valuemin={min}
            aria-valuemax={max}
            {...props}
          />

          {/* Custom thumb */}
          <div
            className={`
              absolute top-1/2 -translate-y-1/2
              w-7 h-7
              rounded-full
              bg-white
              border-[3px] border-magic-400
              shadow-[0_2px_8px_rgba(0,0,0,0.15),0_1px_3px_rgba(0,0,0,0.1)]
              pointer-events-none
              transition-all duration-150
              ${isDragging ? "scale-110 border-magic-500 shadow-[0_4px_12px_rgba(155,89,182,0.3)]" : ""}
              ${disabled ? "opacity-50 border-neutral-300" : ""}
            `}
            style={{
              left: `calc(${percentage}% - 14px)`,
              transitionTimingFunction: isDragging
                ? "linear"
                : "cubic-bezier(0.34, 1.56, 0.64, 1)",
            }}
          >
            {/* Inner glow */}
            <span
              className={`
                absolute inset-1.5 rounded-full
                transition-colors duration-200
                ${value > 0 && !disabled ? "bg-magic-100" : "bg-neutral-100"}
              `}
            />

            {/* Sparkle when dragging */}
            {isDragging && !disabled && (
              <>
                <span className="absolute -top-1 -right-1 w-2 h-2 text-sunshine-400 animate-sparkle">
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 0L14.59 8.41L23 11L14.59 13.59L12 22L9.41 13.59L1 11L9.41 8.41L12 0Z" />
                  </svg>
                </span>
                <span
                  className="absolute -bottom-1 -left-1 w-2 h-2 text-fairy-300 animate-sparkle"
                  style={{ animationDelay: "150ms" }}
                >
                  <svg viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 0L14.59 8.41L23 11L14.59 13.59L12 22L9.41 13.59L1 11L9.41 8.41L12 0Z" />
                  </svg>
                </span>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }
);

Slider.displayName = "Slider";

// Add shimmer animation
if (typeof document !== "undefined") {
  const styleId = "jumpbean-slider-animations";
  if (!document.getElementById(styleId)) {
    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = `
      @keyframes shimmer {
        0% { transform: translateX(-100%); }
        100% { transform: translateX(200%); }
      }
    `;
    document.head.appendChild(style);
  }
}

export default Slider;
